import { Route, SidebarComponent, TopNavBarComponent, useTranslate } from 'sbx-crm-menu-ui';
import React, { useMemo, useState } from 'react';
import { AppAgentContext } from '../../context/ContextProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import { View } from '../../types/Session';
import { capitalize } from '../../utils';
import { faPowerOff, faUser } from '@fortawesome/free-solid-svg-icons';
import { logoutService } from 'src/services/AuthService';

const languages = ['en', 'es', 'nl'];

export const validatePermission = (permission: string | string[], permissions: Record<string, string>) => {
    return Array.isArray(permission) ? permission.some(ss => permissions[ss]) : !!permissions[permission];
};

export const generateSubMenuData = (
    labels: View[],
    callBackPath: (v: View) => void,
    ruleSelection?: (v: View) => boolean,
    nameTranslated?: (name: string) => string,
    isVisible?: (v: View) => boolean,
): Route[] => {
    return labels.map((label) => ({
        label: nameTranslated ? nameTranslated(label.name) : label.name,
        className: 'crm-class-ml-20',
        action: () => callBackPath(label),
        path: label.root ? `${label.root + label.path}` : label.path,
        visible: isVisible?.(label),
        selected: ruleSelection?.(label) || false,
        subRoutes: generateSubMenuData(
            label.views || [],
            callBackPath,
            ruleSelection,
            nameTranslated,
            isVisible,
        ),
    }));
};

export default function MenuLayout(props: { children: React.ReactNode }) {

    const { state: { session } } = React.useContext(AppAgentContext);

    const [openSideBar, setOpenSideBar] = useState(false);

    const { t } = useTranslate('routes');
    const push = useNavigate();

    const navigate = (path: string) => {
        push(path);
        setOpenSideBar(false);
    };
    const location = useLocation();


    const routes: Route[] = useMemo(() => {
        return [
            {
                label: 'Agents',
                path: '/ai-studio/agent',
                className: 'crm-class-ml-20',
                selected: location.pathname === '/agent',
                action: () => {
                    navigate('/agent');
                },
            },
            {
                label: 'Documents',
                path: '/ai-studio/document',
                className: 'crm-class-ml-20',
                selected: location.pathname === '/document',
                action: () => {
                    navigate('/document');
                },
            },
            {
                label: 'Speech to text',
                path: '/ai-studio/speech-to-text/list',
                className: 'crm-class-ml-20',
                selected: location.pathname === '/speech-to-text/list',
                action: () => {
                    navigate('/speech-to-text/list');
                },
            },
            {
                label: 'Images',
                path: '/ai-studio/images',
                className: 'crm-class-ml-20',
                selected: location.pathname === '/images',
                action: () => {
                    navigate('/images');
                },
            },
            {
                label: 'Marketing Agent',
                path: '/ai-studio/marketing-agent',
                className: 'crm-class-ml-20',
                selected: location.pathname === '/marketing-agent',
                action: () => {
                    navigate('/marketing-agent');
                },
            },
        ];
    }, [location.pathname]);

    const dataMapped: Route[] = useMemo(() => {
        return session.routes.map(dta => {
            return {
                label: t(dta.name) || capitalize(dta.name),
                iconName: dta.icon,
                expanded: dta.name === 'home',
                visible: validatePermission(dta.permission, session.permissions),
                subRoutes: ((dta.name === 'home' ? routes : [])).concat(
                    ...generateSubMenuData(dta.views ?? [],
                        (v) => {
                            const path = v.root ? `${v.root + v.path}` : v.path;
                            if (path.includes('{origin}'))
                                window.location.href = path.replace('/{origin}', window.location.origin).replace('{origin}', window.location.origin);
                            else
                                window.location.href = window.location.origin + path;
                        },
                        () => false,
                        (name) => t(name) || capitalize(name),
                        (v) => validatePermission(v.permission, session.permissions)
                        ,
                    )),
            } as Route;
        });
    }, [routes, session]);

    return (
        <>
            <TopNavBarComponent
                dropdownOptions={[
                    {
                        label: 'Profile',
                        type: 'path',
                        path: '/profile',
                        icon: faUser,
                    },
                    {
                        label: 'Logout',
                        type: 'click',
                        onClick: logoutService,
                        icon: faPowerOff,
                    },
                ]}
                openSideBar={openSideBar}
                setOpenSideBar={setOpenSideBar}
                logoSrc={session.logo}
                onClickLogo={() => window.location.href = window.location.origin}
                defaultValueLanguage={localStorage.getItem('crm_locale') || session.defaultLocale || 'es'}
                onChooseLanguage={(language) => {
                    localStorage.setItem('crm_locale', language);
                    window.location.reload();
                }}
                languages={languages} user={{ name: session.full_name, email: session.email }} />
            <SidebarComponent setOpenSideBar={setOpenSideBar} openSideBar={openSideBar} routes={dataMapped}>
                {props.children}
            </SidebarComponent>
        </>
    );
}